import { motion } from 'framer-motion';
import { useAuthorController } from '@/controllers/author/useAuthorController';

const CANDLES = [
  { id: 'front', height: 74, delay: 0 },
  { id: 'middle', height: 58, delay: 0.18 },
  { id: 'back', height: 88, delay: 0.34 },
];

function IconPanel({ side }: { side: 'left' | 'right' }) {
  return (
    <div
      aria-hidden='true'
      className={`pointer-events-none absolute inset-y-0 ${side}-0 hidden w-44 items-center justify-center lg:flex`}
    >
      <div className='relative h-[72vh] max-h-[620px] min-h-[420px] w-28 overflow-hidden border border-[#9e7738]/25 bg-[#1a1109]/65 shadow-[0_0_70px_rgba(120,76,22,0.16)]'>
        <div className='absolute inset-2 border border-[#4f3518]/55' />
        <div
          className='absolute inset-x-4 top-8 h-40 border border-[#b4883d]/20 bg-[#28190c]'
          style={{
            background:
              'radial-gradient(circle at 50% 32%, rgba(236,194,103,0.22), transparent 34%), linear-gradient(180deg, rgba(55,33,13,0.96), rgba(28,17,9,0.98))',
          }}
        >
          <div className='absolute left-1/2 top-7 flex size-16 -translate-x-1/2 items-center justify-center rounded-full border border-[#d0a452]/25 bg-[#523515]/65 text-3xl text-[#d9b769]'>
            ☦
          </div>
          <div className='absolute bottom-5 left-1/2 h-8 w-10 -translate-x-1/2 rounded-t-full border border-[#b4883d]/25 bg-[#65451d]/35' />
        </div>
        <div className='absolute inset-x-5 bottom-10 flex flex-col gap-4'>
          <div className='h-px bg-[#8d6730]/40' />
          <div className='mx-auto size-3 rotate-45 border border-[#b4883d]/45 bg-[#2b1b0d]' />
          <div className='h-px bg-[#8d6730]/40' />
        </div>
      </div>
    </div>
  );
}

function CandleGroup({ side }: { side: 'left' | 'right' }) {
  const positionClass = side === 'left' ? 'left-6 lg:left-20' : 'right-6 lg:right-20';

  return (
    <div
      aria-hidden='true'
      className={`pointer-events-none absolute bottom-14 hidden items-end gap-3 sm:flex ${positionClass}`}
    >
      {CANDLES.map((candle) => (
        <div key={candle.id} className='relative flex flex-col items-center'>
          <motion.div
            animate={{ opacity: [0.74, 1, 0.82], scale: [0.92, 1.07, 0.96] }}
            transition={{ duration: 1.7, delay: candle.delay, repeat: Infinity, ease: 'easeInOut' }}
            className='mb-1 h-7 w-4 rounded-[50%_50%_45%_45%] bg-[#f0a735]'
            style={{ boxShadow: '0 0 30px rgba(240,167,53,0.62), 0 0 72px rgba(202,111,24,0.24)' }}
          />
          <div
            className='w-4 rounded-t-sm border-x border-[#e8d6a3]/35 bg-gradient-to-b from-[#f5e6b8] via-[#d8bb72] to-[#8d6027] shadow-[0_0_22px_rgba(219,165,70,0.18)]'
            style={{ height: candle.height }}
          />
          <div className='h-2 w-7 rounded-full bg-[#6b431a]' />
        </div>
      ))}
    </div>
  );
}

export function AuthorPage() {
  const { verse } = useAuthorController();

  return (
    <div className='min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden bg-[#110c08]'>
      <div className='absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,_rgba(142,102,39,0.18)_0%,_rgba(17,12,8,0)_58%)]' />
      <div className='absolute inset-4 pointer-events-none border border-[#8e6627]/25' />
      <div className='absolute inset-7 pointer-events-none border border-[#4d3518]/30' />
      <IconPanel side='left' />
      <IconPanel side='right' />
      <CandleGroup side='left' />
      <CandleGroup side='right' />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: 'easeOut' }}
        className='flex flex-col items-center text-center gap-7 z-10 max-w-2xl w-full'
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className='relative flex items-center justify-center size-24 rounded-full border border-[#b98b3d]/35 bg-[#1f160d]/80 shadow-[0_0_60px_rgba(185,139,61,0.14)]'
        >
          <span className='text-[#d5ad5c] text-5xl leading-none'>☦</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className='flex flex-col gap-5'
        >
          <p className='font-serif text-xs tracking-[0.32em] uppercase text-[#8e6627]'>
            молитвенное слово
          </p>
          <p className='font-serif leading-relaxed text-2xl md:text-3xl text-[#ead7a3]'>
            {verse?.text ?? 'Загрузка...'}
          </p>
          <p className='font-serif text-sm tracking-[0.24em] uppercase text-[#caa04d]'>
            {verse?.reference ?? 'Синодальный перевод'}
          </p>
        </motion.div>

        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className='flex items-center gap-3 w-full'
        >
          <div
            className='flex-1 h-px'
            style={{ background: 'linear-gradient(to right, transparent, #6b5030)' }}
          />
          <span className='font-serif text-[#8e6627] text-xs'>Господи, помилуй</span>
          <div
            className='flex-1 h-px'
            style={{ background: 'linear-gradient(to left, transparent, #6b5030)' }}
          />
        </motion.div>
      </motion.div>

      <motion.a
        href='/clients'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className='absolute bottom-8 font-serif text-xs tracking-[0.28em] text-[#5f421d] transition-colors hover:text-[#9c7734]'
      >
        ← вернуться
      </motion.a>
    </div>
  );
}
